import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Package, ChevronDown, ChevronRight, Plus, Truck, Upload, AlertTriangle, RefreshCw, RotateCcw, Download } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, fmtDateTime, istToday } from "@/lib/dateFormat";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLORS = {
  "In Stock": "bg-green-100 text-green-800",
  "Low Stock": "bg-amber-100 text-amber-800",
  "Out of Stock": "bg-red-100 text-red-800",
  "Expiry Risk": "bg-orange-100 text-orange-800",
};

export default function SSInventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin");
  const userPerms = user?.permissions || [];
  const _roleBase = isAdmin || (user?.roles || []).some(r => ["Finance Manager", "Finance Executive"].includes(r));
  const canRecordOpeningStock = _roleBase || userPerms.includes("ss_inventory.record_opening_stock");
  const canSubmitAdjustment = _roleBase || userPerms.includes("ss_inventory.adjust_stock");
  const canManageSSStock = canRecordOpeningStock || canSubmitAdjustment || userPerms.includes("ss_inventory.record_outflow");
  const canRecordOutflow = isAdmin || (user?.roles || []).some(r => ["Auditor", "Finance Manager"].includes(r)) || userPerms.includes("ss_inventory.record_outflow");
  const canRecordDelivery = isAdmin || userPerms.includes("ss_inventory.record_platform_delivery");
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const PLATFORMS = getValues("ss_platforms");
  const SHORT_REASONS = getValues("short_delivery_reasons");

  const [ssList, setSsList] = useState([]);
  const [selectedSS, setSelectedSS] = useState("");
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSku, setExpandedSku] = useState(null);
  const [ledgerData, setLedgerData] = useState({});
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [reorderStatus, setReorderStatus] = useState(null);
  const [showOpeningStock, setShowOpeningStock] = useState(false);
  const [openingStockLines, setOpeningStockLines] = useState([]);
  const [openingSkuList, setOpeningSkuList] = useState([]);
  const [openingSkuSearch, setOpeningSkuSearch] = useState("");
  const [openingDate, setOpeningDate] = useState(istToday());
  const [savingOpening, setSavingOpening] = useState(false);

  // Delivery form
  const [deliveryForm, setDeliveryForm] = useState({
    platform: "", platform_po_number: "", delivery_date: istToday(),
    appointment_datetime: "", platform_grn_number: "", notes: "",
    line_items: [],
  });
  const [submitting, setSubmitting] = useState(false);

  // SS Pulse Sync: Gap view, Pending adjustments, Adjustment form
  const [gapData, setGapData] = useState(null);
  const [pendingAdj, setPendingAdj] = useState([]);
  const [pendingOutflows, setPendingOutflows] = useState([]);
  const [allOutflows, setAllOutflows] = useState([]);
  const [showAdjForm, setShowAdjForm] = useState(null); // holds {ss_id, sku_id, sku_name, batch, new_balance, rc_id}
  const [adjReason, setAdjReason] = useState("");
  const [adjReasonText, setAdjReasonText] = useState("");
  const [adjNewBal, setAdjNewBal] = useState(0);
  const ADJ_REASONS = getValues("ss_adjustment_reasons");

  useEffect(() => {
    api.get("/ss-inventory/ss-list").then(r => setSsList(r.data)).catch(err => console.error("API get /ss-inventory/ss-list error:", err));
  }, []);

  const loadStock = useCallback(async (ssId) => {
    if (!ssId) return;
    setLoading(true);
    try {
      const [stockRes, reorderRes] = await Promise.all([
        api.get(`/ss-inventory/stock/${ssId}`),
        api.get(`/ss-inventory/reorder-status/${ssId}`),
      ]);
      setStockData(stockRes.data);
      setReorderStatus(reorderRes.data);
      // Load gap data + pending adjustments
      api.get(`/ss-pulse-sync/gap/${ssId}`).then(r => setGapData(r.data)).catch(() => setGapData(null));
      if (isAdmin) api.get("/ss-inventory/adjustments", { params: { status: "Pending Admin Approval" } }).then(r => setPendingAdj(r.data)).catch(() => {});
      if (isAdmin) api.get("/ss-inventory/outflows", { params: { status: "Pending Approval" } }).then(r => setPendingOutflows(r.data)).catch(() => {});
      api.get("/ss-inventory/outflows").then(r => setAllOutflows(r.data)).catch(() => {});
    } catch (err) { console.error("SSInventoryPage error:", err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedSS) loadStock(selectedSS); }, [selectedSS, loadStock]);

  const loadLedger = async (skuId) => {
    if (!selectedSS) return;
    if (expandedSku === skuId) { setExpandedSku(null); return; }
    setExpandedSku(skuId);
    if (ledgerData[skuId]) return;
    try {
      const { data } = await api.get(`/ss-inventory/ledger/${selectedSS}/${skuId}`);
      setLedgerData(prev => ({ ...prev, [skuId]: data }));
    } catch (err) { console.error("SSInventoryPage error:", err); }
  };

  const openDeliveryForm = () => {
    const items = stockData.filter(s => s.current_balance > 0).map(s => ({
      sku_id: s.sku_id, sku_name: s.sku_name,
      available_batches: (s.batches || []).map(b => ({
        batch_number: b.batch_number || "default",
        available_qty: b.quantity || 0,
        checked: (s.batches || []).length === 1, // Auto-check if single batch
        qty_accepted: 0,
        reason: "",
      })),
      quantity_po: 0,
    }));
    setDeliveryForm({
      platform: "", platform_po_number: "",
      delivery_date: istToday(),
      appointment_datetime: "", platform_grn_number: "", notes: "",
      line_items: items,
    });
    setShowDeliveryDialog(true);
  };

  const updateBatchRow = (skuIdx, batchIdx, field, value) => {
    const items = [...deliveryForm.line_items];
    const batches = [...items[skuIdx].available_batches];
    batches[batchIdx] = { ...batches[batchIdx], [field]: value };
    if (field === "checked" && !value) batches[batchIdx].qty_accepted = 0;
    items[skuIdx] = { ...items[skuIdx], available_batches: batches };
    setDeliveryForm(prev => ({ ...prev, line_items: items }));
  };

  const getSkuTotalAccepted = (item) => item.available_batches.filter(b => b.checked).reduce((s, b) => s + (parseInt(b.qty_accepted) || 0), 0);

  const submitDelivery = async () => {
    if (!deliveryForm.platform_grn_number) { toast.error("Platform GRN Number is mandatory"); return; }
    const activeItems = [];
    for (const li of deliveryForm.line_items) {
      const checkedBatches = li.available_batches.filter(b => b.checked && parseInt(b.qty_accepted) > 0);
      if (checkedBatches.length === 0) continue;
      for (const b of checkedBatches) {
        if (parseInt(b.qty_accepted) > b.available_qty) {
          toast.error(`${li.sku_name}: Qty (${b.qty_accepted}) exceeds available (${b.available_qty}) for batch ${b.batch_number}`);
          return;
        }
      }
      const totalAccepted = checkedBatches.reduce((s, b) => s + (parseInt(b.qty_accepted) || 0), 0);
      for (const b of checkedBatches) {
        activeItems.push({ sku_id: li.sku_id, sku_name: li.sku_name, batch_number: b.batch_number, quantity_po: parseInt(li.quantity_po) || 0, quantity_accepted: parseInt(b.qty_accepted) || 0, variance: (parseInt(li.quantity_po) || 0) - totalAccepted, short_reason: b.reason || "" });
      }
    }
    if (activeItems.length === 0) { toast.error("At least one batch must have quantity accepted"); return; }
    setSubmitting(true);
    try {
      const ss = ssList.find(s => s._id === selectedSS);
      await api.post("/ss-inventory/record-delivery", { ss_customer_id: selectedSS, ss_name: ss?.company_name || ss?.name || "", platform: deliveryForm.platform, platform_po_number: deliveryForm.platform_po_number, delivery_date: deliveryForm.delivery_date, appointment_datetime: deliveryForm.appointment_datetime, line_items: activeItems, platform_grn_number: deliveryForm.platform_grn_number, notes: deliveryForm.notes });
      toast.success("Platform delivery recorded");
      setShowDeliveryDialog(false); setLedgerData({}); loadStock(selectedSS);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const ssName = (() => { const ss = ssList.find(s => s._id === selectedSS); return ss ? (ss.company_name || ss.name) : ""; })();

  return (
    <div data-testid="ss-inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">SS Inventory</h1>
          <p className="text-sm text-zinc-500 mt-1">Qcom Super Stockist stock tracking</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedSS && <Button onClick={() => loadStock(selectedSS)} variant="outline" className="rounded-sm h-8 text-xs" data-testid="ss-refresh"><RefreshCw size={14} /></Button>}
          <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={async () => { try { const { data } = await api.get("/ss-inventory/export-excel", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data])); a.download = `SS_Inventory_${istToday()}.xlsx`; a.click(); } catch { toast.error("Export failed"); } }} data-testid="ss-export-excel"><Download size={14} className="mr-1" />Download Excel</Button>
          {selectedSS && canRecordOpeningStock && (
            <Button variant="outline" onClick={() => {
              setOpeningStockLines([]);
              setOpeningDate(istToday());
              api.get("/catalogue/skus?status=Active").then(({ data }) => setOpeningSkuList(data)).catch(() => {});
              setShowOpeningStock(true);
            }} className="rounded-sm h-8 text-xs" data-testid="add-opening-stock-btn">
              <Plus size={14} /> Add Opening Stock
            </Button>
          )}
          {selectedSS && stockData.length > 0 && canRecordDelivery && (
            <Button onClick={() => {
              const ss = ssList.find(s => s._id === selectedSS);
              navigate(`/ss-inventory/record-delivery?ss_id=${selectedSS}&ss_name=${encodeURIComponent(ss?.company_name || ss?.name || "")}`);
            }} className="rounded-sm h-8 text-xs" data-testid="record-delivery-btn">
              <Truck size={14} /> Record Platform Delivery
            </Button>
          )}
          {selectedSS && canRecordOutflow && (
            <Button variant="outline" onClick={() => {
              const ss = ssList.find(s => s._id === selectedSS);
              navigate(`/ss-inventory/record-outflow?ss_id=${selectedSS}&ss_name=${encodeURIComponent(ss?.company_name || ss?.name || "")}`);
            }} className="rounded-sm h-8 text-xs" data-testid="record-outflow-btn">
              Record Outflow
            </Button>
          )}
          {canRecordOutflow && (
            <Button variant="outline" onClick={() => navigate("/ss-inventory/reset")} className="rounded-sm h-8 text-xs" data-testid="reset-stock-btn">
              <RotateCcw size={14} className="mr-1" />Reset Stock
            </Button>
          )}
        </div>
      </div>

      {/* SS Selector */}
      <div className="mb-6 max-w-sm">
        <Label className="text-xs">Select Super Stockist</Label>
        <Select value={selectedSS || "none"} onValueChange={(v) => { setSelectedSS(v === "none" ? "" : v); setExpandedSku(null); setLedgerData({}); }}>
          <SelectTrigger className="mt-1 rounded-sm" data-testid="ss-selector">
            <SelectValue placeholder="Select an SS" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- Select --</SelectItem>
            {ssList.map(ss => <SelectItem key={ss._id} value={ss._id}>{ss.company_name || ss.name}{ss.company_name && ss.name ? ` / ${ss.name}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {ssList.length === 0 && !loading && (
        <div className="text-center py-12 border border-dashed border-zinc-300 rounded-sm" data-testid="no-ss-message">
          <Package size={32} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-400">No Qcom Super Stockist customers found.</p>
          <p className="text-xs text-zinc-400 mt-1">Create a customer with type "Qcom Super Stockist" to get started.</p>
        </div>
      )}

      {selectedSS && loading && <p className="text-center py-8 text-zinc-400 text-sm">Loading stock data...</p>}

      {selectedSS && !loading && (
        <>
          {/* Reorder Warning Banner */}
          {reorderStatus?.has_pending_draft && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-sm flex items-start gap-2" data-testid="reorder-draft-banner">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">A draft reorder order <span className="font-mono">{reorderStatus.draft_order_number}</span> already exists for this SS</p>
                <p className="text-xs text-amber-700 mt-0.5">Please review and submit it before a new one can be generated.</p>
              </div>
              <Button variant="outline" className="rounded-sm h-7 text-xs border-amber-400 text-amber-800" onClick={() => window.location.href = `/orders/${reorderStatus.draft_order_id}`} data-testid="view-draft-order-btn">
                View Draft
              </Button>
            </div>
          )}

          {/* Stock Position Table */}
          <div className="border border-zinc-200 overflow-x-auto" data-testid="stock-position-table">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="w-8"></th>
                  <th>SKU Code</th>
                  <th>SKU Name</th>
                  <th className="num">Balance</th>
                  <th>Last Stock IN</th>
                  <th>Last Delivery OUT</th>
                  <th>Oldest Expiry</th>
                  <th className="num">Days to Expiry</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stockData.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">No stock data for this SS. Stock will appear when orders are delivered.</td></tr>
                ) : stockData.map((item) => (
                  <>
                    <tr key={item.sku_id} className="cursor-pointer hover:bg-zinc-50" onClick={() => loadLedger(item.sku_id)} data-testid={`stock-row-${item.sku_id}`}>
                      <td className="text-center">
                        {expandedSku === item.sku_id ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
                      </td>
                      <td className="font-mono text-xs">{item.sku_id}</td>
                      <td className="text-xs font-medium">{item.sku_name}</td>
                      <td className="num font-mono font-semibold">{item.current_balance}</td>
                      <td className="text-xs text-zinc-500">{item.last_stock_in ? fmtDate(item.last_stock_in) : "—"}</td>
                      <td className="text-xs text-zinc-500">{item.last_delivery_out ? fmtDate(item.last_delivery_out) : "—"}</td>
                      <td className="text-xs text-zinc-500">{item.oldest_batch_expiry ? fmtDate(item.oldest_batch_expiry) : "—"}</td>
                      <td className={`num font-mono text-xs ${item.days_until_expiry !== null && item.days_until_expiry <= 30 ? 'text-red-600 font-semibold' : ''}`}>
                        {item.days_until_expiry !== null ? item.days_until_expiry : "—"}
                      </td>
                      <td>
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${STATUS_COLORS[item.status] || 'bg-zinc-100 text-zinc-600'}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                    {expandedSku === item.sku_id && (
                      <tr key={`${item.sku_id}-ledger`}>
                        <td colSpan={9} className="bg-zinc-50 p-0">
                          <div className="px-6 py-3" data-testid={`ledger-${item.sku_id}`}>
                            <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-1">Stock Ledger — {item.sku_name}</p>
                            {!ledgerData[item.sku_id] ? (
                              <p className="text-xs text-zinc-400">Loading...</p>
                            ) : ledgerData[item.sku_id].length === 0 ? (
                              <p className="text-xs text-zinc-400">No ledger entries</p>
                            ) : (() => {
                              const entries = ledgerData[item.sku_id];
                              // Find last reset entry for "Last reconciled" stamp
                              const lastReset = entries.find(e => e.entry_type === "reset" || e.type === "RESET");
                              // Group by normalized batch
                              const batchGroups = {};
                              for (const e of entries) {
                                const bn = (e.batch_number || "").toUpperCase().replace(/[^A-Z0-9]/g, "") || "(no batch)";
                                if (!batchGroups[bn]) batchGroups[bn] = [];
                                batchGroups[bn].push(e);
                              }
                              return (
                                <>
                                  {lastReset && <p className="text-[9px] text-blue-600 mb-2">Last reconciled by {lastReset.recorded_by_name || lastReset.recorded_by || "—"} on {fmtDate(lastReset.recorded_at || lastReset.timestamp)}</p>}
                                  {Object.entries(batchGroups).map(([bn, bEntries]) => {
                                    // Sort by recorded_at (canonical), fallback timestamp
                                    const sorted = [...bEntries].sort((a, b) => (a.recorded_at || a.timestamp || "").localeCompare(b.recorded_at || b.timestamp || ""));
                                    let runBal = 0;
                                    const rows = sorted.map(e => {
                                      const rawQ = e.quantity || 0;
                                      const isIn = e.entry_type === "stock_in" || e.entry_type === "opening" || ["IN", "OPENING"].includes(e.type) || (e.entry_type === "" && !e.type && e.quantity > 0);
                                      const isReset = e.entry_type === "reset" || e.type === "RESET";
                                      const isAdj = e.entry_type === "adjustment" || e.type === "ADJUSTMENT";
                                      // RESET + ADJUSTMENT: use signed qty directly. IN adds, OUT subtracts.
                                      const movement = (isReset || isAdj) ? rawQ : (isIn ? Math.abs(rawQ) : -Math.abs(rawQ));
                                      runBal += movement;
                                      return { ...e, _isIn: (isReset || isAdj) ? rawQ >= 0 : isIn, _q: Math.abs(rawQ), _movement: movement, _runBal: runBal, _isReset: isReset, _isAdj: isAdj };
                                    });
                                    const batchNet = runBal;
                                    return (
                                      <div key={bn} className="mb-3">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-[9px] font-semibold font-mono bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded">{bn}</span>
                                          <span className={`text-[9px] font-semibold ${batchNet > 0 ? "text-green-700" : batchNet < 0 ? "text-red-600" : "text-zinc-400"}`}>Net: {batchNet}</span>
                                        </div>
                                        <table className="w-full text-xs">
                                          <thead><tr className="border-b border-zinc-200"><th className="text-left py-1 px-2 text-[10px] font-semibold text-zinc-400">Date</th><th className="text-left py-1 px-2 text-[10px] font-semibold text-zinc-400">Type</th><th className="text-right py-1 px-2 text-[10px] font-semibold text-zinc-400">Qty</th><th className="text-right py-1 px-2 text-[10px] font-semibold text-zinc-400">Balance</th><th className="text-left py-1 px-2 text-[10px] font-semibold text-zinc-400">Reference</th><th className="text-left py-1 px-2 text-[10px] font-semibold text-zinc-400">Notes</th></tr></thead>
                                          <tbody>
                                            {rows.map((e, i) => (
                                              <tr key={i} className="border-b border-zinc-100">
                                                <td className="py-1 px-2 text-zinc-500">{fmtDateTime(e.recorded_at || e.timestamp)}</td>
                                                <td className="py-1 px-2"><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${e._isReset ? "bg-purple-100 text-purple-700" : e._isAdj ? "bg-amber-100 text-amber-700" : e._isIn ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{e._isReset ? "RESET" : e._isAdj ? "ADJ" : e._isIn ? "IN" : "OUT"}</span></td>
                                                <td className={`py-1 px-2 text-right font-mono font-semibold ${e._movement >= 0 ? "text-green-700" : "text-red-600"}`}>{e._movement >= 0 ? "+" : ""}{e._movement}</td>
                                                <td className="py-1 px-2 text-right font-mono text-zinc-600">{e._runBal}</td>
                                                <td className="py-1 px-2 text-zinc-500">{e.order_number || e.platform_po_number || e.reference_id || "—"}</td>
                                                <td className="py-1 px-2 text-zinc-400">{e.notes || "—"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Record Platform Delivery Dialog */}
      <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Platform Delivery</DialogTitle>
            <p className="text-xs text-zinc-500">SS: {ssName}</p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Platform</Label>
                <Select value={deliveryForm.platform || "none"} onValueChange={(v) => setDeliveryForm(prev => ({ ...prev, platform: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="delivery-platform"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select --</SelectItem>
                    {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Platform PO Number</Label>
                <Input value={deliveryForm.platform_po_number} onChange={(e) => setDeliveryForm(prev => ({ ...prev, platform_po_number: e.target.value }))} className="mt-1 rounded-sm" data-testid="delivery-po" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Delivery Date</Label>
                <Input type="date" value={deliveryForm.delivery_date} onChange={(e) => setDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs">Appointment Date & Time (optional)</Label>
                <Input type="datetime-local" value={deliveryForm.appointment_datetime} onChange={(e) => setDeliveryForm(prev => ({ ...prev, appointment_datetime: e.target.value }))} className="mt-1 rounded-sm" />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <Label className="text-xs font-semibold">SKU Delivery Details</Label>
              <div className="border border-zinc-200 mt-2 overflow-x-auto">
                <div className="space-y-2">
                  {(deliveryForm.line_items || []).map((li, idx) => {
                    const totalAcc = getSkuTotalAccepted(li);
                    return (
                      <div key={idx} className="border border-zinc-200 rounded-sm" data-testid={`delivery-line-${idx}`}>
                        <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-100">
                          <span className="text-xs font-medium">{li.sku_name} <span className="text-zinc-400 font-mono">({li.sku_id})</span></span>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-zinc-500">PO Qty:</span><Input type="number" min={0} value={li.quantity_po || ""} onChange={(e) => { const items = [...deliveryForm.line_items]; items[idx] = { ...items[idx], quantity_po: e.target.value }; setDeliveryForm(prev => ({ ...prev, line_items: items })); }} className="h-6 w-16 text-xs text-right rounded-sm" />
                            <span className="font-mono font-semibold text-blue-700">Accepted: {totalAcc}</span>
                          </div>
                        </div>
                        <div className="px-3 py-1.5">
                          {(li.available_batches || []).map((b, bi) => (
                            <div key={bi} className={`flex items-center gap-2 py-1.5 border-b border-zinc-50 last:border-0 ${b.checked ? '' : 'opacity-40'}`} data-testid={`batch-row-${idx}-${bi}`}>
                              <input type="checkbox" checked={b.checked || false} onChange={(e) => updateBatchRow(idx, bi, "checked", e.target.checked)} className="w-3.5 h-3.5 accent-blue-600 shrink-0" />
                              <span className="text-[11px] font-mono w-28 truncate" title={b.batch_number}>{b.batch_number === "default" || b.batch_number === "N/A" ? "Default Batch" : b.batch_number}</span>
                              <span className="text-[10px] text-zinc-400 w-16 shrink-0">({b.available_qty})</span>
                              <Input type="number" min={0} max={b.available_qty} value={b.qty_accepted || ""} disabled={!b.checked} onChange={(e) => updateBatchRow(idx, bi, "qty_accepted", parseInt(e.target.value) || 0)} className={`h-6 w-16 text-xs text-right rounded-sm ${!b.checked ? 'bg-zinc-100 cursor-not-allowed' : ''}`} placeholder="Qty" />
                              {b.checked && parseInt(b.qty_accepted) > 0 && parseInt(b.qty_accepted) < b.available_qty && (
                                <Input value={b.reason || ""} onChange={(e) => updateBatchRow(idx, bi, "reason", e.target.value)} placeholder="Short reason" className="h-6 text-[10px] rounded-sm flex-1" />
                              )}
                              {b.checked && parseInt(b.qty_accepted) > b.available_qty && <span className="text-[10px] text-red-600 font-semibold">Exceeds!</span>}
                            </div>
                          ))}
                          {(li.available_batches || []).length === 0 && <p className="text-[10px] text-zinc-400 py-2">No batches available for this SKU</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Platform GRN Number (mandatory)</Label>
                <Input value={deliveryForm.platform_grn_number} onChange={(e) => setDeliveryForm(prev => ({ ...prev, platform_grn_number: e.target.value }))} className="mt-1 rounded-sm" data-testid="delivery-grn" placeholder="e.g. GRN-12345" />
              </div>
              <div>
                <Label className="text-xs">Upload Platform GRN Document</Label>
                <Input type="file" accept=".jpg,.jpeg,.png,.pdf" className="mt-1 rounded-sm text-xs" data-testid="delivery-grn-upload" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={deliveryForm.notes} onChange={(e) => setDeliveryForm(prev => ({ ...prev, notes: e.target.value }))} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliveryDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={submitDelivery} disabled={submitting} className="rounded-sm text-xs" data-testid="submit-delivery-btn">
              {submitting ? "Saving..." : "Record Delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opening Stock Dialog */}
      <Dialog open={showOpeningStock} onOpenChange={setShowOpeningStock}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="opening-stock-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Opening Stock — {ssName}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Effective Date</Label><Input type="date" value={openingDate} onChange={e => setOpeningDate(e.target.value)} className="mt-1 h-8 text-xs rounded-sm w-40" /></div>
            <div className="relative">
              <Input value={openingSkuSearch} onChange={e => setOpeningSkuSearch(e.target.value)} placeholder="Type SKU name or code to search..." className="h-8 text-xs rounded-sm w-80" data-testid="opening-sku-search" />
              {openingSkuSearch.trim().length > 0 && (() => {
                const q = openingSkuSearch.trim().toLowerCase();
                const addedIds = new Set(openingStockLines.map(l => l.sku_id));
                const filtered = openingSkuList.filter(s => !addedIds.has(s.sku_id) && (s.sku_name.toLowerCase().includes(q) || s.sku_id.toLowerCase().includes(q)));
                return <div className="absolute z-50 mt-1 w-80 max-h-48 overflow-y-auto bg-white border border-zinc-200 rounded-sm shadow-lg">
                  {filtered.length === 0 ? <div className="px-3 py-2 text-xs text-zinc-400">No matching SKU</div> : filtered.map(s => (
                    <div key={s.sku_id} className="px-3 py-1.5 text-xs hover:bg-zinc-100 cursor-pointer" onClick={() => { setOpeningStockLines(p => [...p, { sku_id: s.sku_id, sku_name: s.sku_name, batch_number: "", qty: 0, notes: "" }]); setOpeningSkuSearch(""); }}>{s.sku_name} <span className="text-zinc-400 font-mono">({s.sku_id})</span></div>
                  ))}
                </div>;
              })()}
            </div>
            {openingStockLines.length === 0 && <p className="text-xs text-zinc-400 py-2">No SKUs added yet. Select from the dropdown above.</p>}
            {openingStockLines.length > 0 && <div className="border border-zinc-200 overflow-x-auto rounded-sm">
              <table className="data-table w-full"><thead><tr><th>SKU Name</th><th>SKU Code</th><th>Batch Number</th><th className="num">Opening Qty</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {openingStockLines.map((line, i) => (
                  <tr key={i}>
                    <td className="text-xs">{line.sku_name}</td>
                    <td className="font-mono text-xs">{line.sku_id}</td>
                    <td><Input value={line.batch_number || ""} onChange={e => { const ls = [...openingStockLines]; ls[i] = {...ls[i], batch_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')}; setOpeningStockLines(ls); }} className="h-7 text-xs rounded-sm w-32" placeholder="e.g. SG139" /></td>
                    <td className="num"><Input type="number" min={0} value={line.qty || ""} onChange={e => { const ls = [...openingStockLines]; ls[i] = {...ls[i], qty: parseInt(e.target.value) || 0}; setOpeningStockLines(ls); }} className="h-7 w-20 text-xs text-right rounded-sm" /></td>
                    <td><Input value={line.notes || ""} onChange={e => { const ls = [...openingStockLines]; ls[i] = {...ls[i], notes: e.target.value}; setOpeningStockLines(ls); }} className="h-7 text-xs rounded-sm w-32" placeholder="Optional" /></td>
                    <td><button onClick={() => setOpeningStockLines(p => p.filter((_, j) => j !== i))} className="p-1 hover:bg-red-50 rounded-sm text-red-400 text-xs">✕</button></td>
                  </tr>
                ))}
              </tbody></table>
            </div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpeningStock(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button disabled={savingOpening} onClick={async () => {
              const validLines = openingStockLines.filter(l => l.qty > 0);
              if (validLines.length === 0) { toast.error("Enter quantity for at least one SKU"); return; }
              setSavingOpening(true);
              try {
                const { data } = await api.post("/ss-inventory/opening-stock", { ss_customer_id: selectedSS, effective_date: openingDate, reference_note: `Opening stock as on ${openingDate}`, lines: validLines });
                toast.success(data.message);
                if (data.skipped?.length > 0) toast(`Skipped duplicates: ${data.skipped.join(", ")}`, { duration: 6000 });
                setShowOpeningStock(false);
                loadStock(selectedSS);
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              finally { setSavingOpening(false); }
            }} className="rounded-sm text-xs" data-testid="save-opening-stock">{savingOpening ? "Saving..." : "Save Opening Stock"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Gap View with Submit as Adjustment ═══ */}
      {gapData && gapData.gaps?.length > 0 && selectedSS && (
        <div className="mt-6 border border-zinc-200 rounded-sm p-4" data-testid="gap-view">
          <h2 className="text-sm font-semibold text-zinc-800 mb-3">Blinkit GRN Gap Analysis — {gapData.ss_name}</h2>
          <div className="overflow-x-auto"><table className="data-table w-full"><thead><tr><th>PO#</th><th>SKU</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Gap</th><th>Batches</th><th></th></tr></thead><tbody>
            {gapData.gaps.map((g, i) => (
              <tr key={i} className={g.gap_qty > 0 ? "bg-amber-50" : ""}>
                <td className="text-xs font-mono">{g.po_number}</td>
                <td className="text-xs">{g.item_name}</td>
                <td className="text-right font-mono text-xs">{g.ordered_qty}</td>
                <td className="text-right font-mono text-xs">{g.received_qty}</td>
                <td className="text-right font-mono text-xs font-semibold">{g.gap_qty > 0 ? <span className="text-red-600">{g.gap_qty}</span> : "0"}</td>
                <td className="text-[10px]">{g.batches?.map((b, bi) => (
                  <div key={bi} className={`py-0.5 ${b.is_unmatched ? "text-amber-600" : ""}`}>
                    {b.batch_number}: sys={b.system_qty}
                    {b.reported && <span className="ml-1 text-blue-600">rep={b.reported.reported_qty}</span>}
                    {b.reported && !b.reported.is_unmatched_batch && b.system_qty !== b.reported.reported_qty && (
                      canSubmitAdjustment && !b.reported.actioned ? (
                        <button onClick={() => { setShowAdjForm({ss_id: selectedSS, sku_id: g.sku_id, sku_name: g.item_name, batch: b.batch_number, new_balance: b.reported.reported_qty, rc_id: b.reported._id || ""}); setAdjNewBal(b.reported.reported_qty); setAdjReason(""); setAdjReasonText(""); }} className="ml-1 px-1 py-0.5 text-[9px] bg-blue-100 text-blue-700 rounded-sm">Submit Adjustment</button>
                      ) : b.reported.actioned ? <span className="ml-1 text-[9px] text-green-600">Adjustment submitted</span> : null
                    )}
                    {b.is_unmatched && <span className="ml-1 text-[9px] text-amber-600">(unmatched batch)</span>}
                  </div>
                ))}</td>
                <td>{g.needs_review && <span className="px-1 py-0.5 text-[9px] bg-red-100 text-red-600 rounded-sm">Needs review</span>}</td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      )}

      {/* ═══ Pending Adjustments (Admin only) ═══ */}
      {isAdmin && pendingAdj.length > 0 && (
        <div className="mt-6 border border-amber-200 rounded-sm p-4 bg-amber-50" data-testid="pending-adjustments">
          <h2 className="text-sm font-semibold text-amber-800 mb-3">Pending Stock Adjustments ({pendingAdj.length})</h2>
          <div className="overflow-x-auto"><table className="data-table w-full"><thead><tr><th>SS</th><th>SKU</th><th>Batch</th><th className="num">Current</th><th className="num">New</th><th className="num">Diff</th><th>Reason</th><th>By</th><th></th></tr></thead><tbody>
            {pendingAdj.map(a => (
              <tr key={a._id}>
                <td className="text-xs">{a.ss_name}</td><td className="text-xs">{a.sku_name}</td><td className="text-xs font-mono">{a.batch_number}</td>
                <td className="text-right font-mono text-xs">{a.current_balance}</td><td className="text-right font-mono text-xs">{a.new_balance}</td>
                <td className="text-right font-mono text-xs font-semibold">{a.difference > 0 ? "+" : ""}{a.difference}</td>
                <td className="text-[10px] text-zinc-500">{a.reason_category}: {a.reason_text?.substring(0, 40)}</td>
                <td className="text-[10px]">{a.submitted_by}</td>
                <td className="flex gap-1">
                  <Button size="sm" className="h-6 text-[10px] rounded-sm" onClick={async () => {
                    const cnReq = window.confirm("Is a Credit Note required for this adjustment?");
                    try {
                      await api.put(`/ss-inventory/adjust/${a._id}/approve`, { cn_required: cnReq });
                      toast.success("Approved" + (cnReq ? " — CN required" : ""));
                      setPendingAdj(p => p.filter(x => x._id !== a._id));
                    } catch(e) { toast.error("Failed"); }
                  }}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={async () => {
                    const reason = prompt("Rejection reason:");
                    if (!reason) return;
                    try {
                      await api.put(`/ss-inventory/adjust/${a._id}/reject`, { reason });
                      toast.success("Rejected");
                      setPendingAdj(p => p.filter(x => x._id !== a._id));
                    } catch(e) { toast.error("Failed"); }
                  }}>Reject</Button>
                </td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      )}

      {/* ═══ Pending Outflows (Admin only) ═══ */}
      {isAdmin && pendingOutflows.length > 0 && (
        <div className="mt-6 border border-amber-200 rounded-sm p-4 bg-amber-50" data-testid="pending-outflows">
          <h2 className="text-sm font-semibold text-amber-800 mb-3">Pending Outflow Approvals ({pendingOutflows.length})</h2>
          {pendingOutflows.map(o => (
            <div key={o._id} className="border border-amber-300 bg-white rounded-sm p-3 mb-2" data-testid={`pending-outflow-${o._id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-semibold">{o.ss_name}</span>
                  <span className="ml-2 px-1.5 py-0.5 text-[9px] font-medium bg-orange-100 text-orange-700 rounded-sm">{o.outflow_type}</span>
                </div>
                <span className="text-[10px] text-zinc-400">by {o.submitted_by} · {fmtDate(o.submitted_at)}</span>
              </div>
              <p className="text-[10px] text-zinc-600 mb-2">Reason: {o.reason}</p>
              <div className="overflow-x-auto mb-2"><table className="w-full text-xs"><thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px]">SKU</th><th className="px-2 py-1 text-left text-[10px]">Batch</th><th className="px-2 py-1 text-right text-[10px]">Qty</th></tr></thead><tbody>
                {(o.lines || []).map((l, i) => <tr key={i} className="border-t border-zinc-100"><td className="px-2 py-1">{l.sku_name || l.sku_id}</td><td className="px-2 py-1 font-mono">{l.batch_number || "—"}</td><td className="px-2 py-1 text-right font-mono">{l.quantity || l.qty}</td></tr>)}
              </tbody></table></div>
              <div className="flex gap-2">
                <Button size="sm" className="h-6 text-[10px] rounded-sm" data-testid={`approve-outflow-${o._id}`} onClick={async () => {
                  try {
                    await api.put(`/ss-inventory/outflow/${o._id}/approve`);
                    toast.success("Outflow approved, stock ledger updated");
                    setPendingOutflows(p => p.filter(x => x._id !== o._id));
                    api.get("/ss-inventory/outflows").then(r => setAllOutflows(r.data)).catch(() => {});
                    if (selectedSS) loadStock(selectedSS);
                  } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}>Approve</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" data-testid={`reject-outflow-${o._id}`} onClick={async () => {
                  const reason = prompt("Rejection reason:");
                  if (!reason) return;
                  try {
                    await api.put(`/ss-inventory/outflow/${o._id}/reject`, { reason });
                    toast.success("Outflow rejected");
                    setPendingOutflows(p => p.filter(x => x._id !== o._id));
                    api.get("/ss-inventory/outflows").then(r => setAllOutflows(r.data)).catch(() => {});
                  } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ SS Outflows (read-only list, all users) ═══ */}
      {allOutflows.length > 0 && (
        <div className="mt-6 border border-zinc-200 rounded-sm" data-testid="outflows-list">
          <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200"><h2 className="text-sm font-semibold text-zinc-700">SS Outflows ({allOutflows.length})</h2></div>
          <div className="overflow-x-auto"><table className="data-table w-full"><thead><tr><th>SS</th><th>Type</th><th>Reason</th><th className="num">Qty</th><th>Status</th><th>Submitted By</th><th>Date</th></tr></thead><tbody>
            {allOutflows.map(o => {
              const totalQty = (o.lines || []).reduce((s, l) => s + Math.abs(l.quantity || l.qty || 0), 0);
              const sc = o.status === "Approved" ? "bg-green-100 text-green-700" : o.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700";
              return (
                <tr key={o._id}>
                  <td className="text-xs">{o.ss_name}</td>
                  <td className="text-xs">{o.outflow_type}</td>
                  <td className="text-xs text-zinc-500 truncate max-w-[150px]">{o.reason}</td>
                  <td className="num font-mono text-xs">{totalQty}</td>
                  <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${sc}`}>{o.status}</span></td>
                  <td className="text-[10px] text-zinc-500">{o.submitted_by}</td>
                  <td className="text-[10px] text-zinc-500">{fmtDate(o.submitted_at)}</td>
                </tr>
              );
            })}
          </tbody></table></div>
        </div>
      )}

      {/* ═══ Adjustment Form Dialog ═══ */}
      {showAdjForm && (
        <Dialog open={!!showAdjForm} onOpenChange={() => setShowAdjForm(null)}>
          <DialogContent className="max-w-sm" data-testid="adj-form-dialog">
            <DialogHeader><DialogTitle className="text-sm">Submit Stock Adjustment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-[10px] text-zinc-500">{showAdjForm.sku_name} — Batch: {showAdjForm.batch}</p>
              <div><Label className="text-xs">New Balance</Label><Input type="number" value={adjNewBal} onChange={e => setAdjNewBal(parseInt(e.target.value) || 0)} className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Reason Category</Label><Select value={adjReason} onValueChange={setAdjReason}><SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{ADJ_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Detailed Reason (min 10 chars)</Label><Textarea value={adjReasonText} onChange={e => setAdjReasonText(e.target.value)} className="text-xs h-16" placeholder="Explain the discrepancy..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdjForm(null)} className="rounded-sm text-xs">Cancel</Button>
              <Button disabled={submitting} onClick={async () => {
                if (!adjReason) { toast.error("Select a reason category"); return; }
                if (adjReasonText.length < 10) { toast.error("Reason must be at least 10 characters"); return; }
                setSubmitting(true);
                try {
                  await api.post("/ss-inventory/adjust", { ss_customer_id: showAdjForm.ss_id, sku_id: showAdjForm.sku_id, sku_name: showAdjForm.sku_name, batch_number: showAdjForm.batch, new_balance: adjNewBal, reason_category: adjReason, reason_text: adjReasonText, source_reported_count_id: showAdjForm.rc_id || undefined });
                  toast.success("Adjustment submitted for Admin approval");
                  setShowAdjForm(null); loadStock(selectedSS);
                } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
                finally { setSubmitting(false); }
              }} className="rounded-sm text-xs">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
